/**
 * Global Search State Hook
 *
 * Zustand store for managing global search dialog state.
 * Registers ⌘K / Ctrl+K keyboard shortcut.
 */

import { create } from 'zustand';
import { useEffect } from 'react';

interface GlobalSearchStore {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const useGlobalSearchStore = create<GlobalSearchStore>((set) => ({
  isOpen: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
}));

/**
 * Hook to control global search and register keyboard shortcut
 *
 * @example
 * const { isOpen, open, close } = useGlobalSearch();
 */
export function useGlobalSearch() {
  const { isOpen, open, close, toggle } = useGlobalSearchStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return { isOpen, open, close, toggle };
}
